import React from 'react';
import { FormGroup, FormField, Dropdown, TextArea, List } from 'semantic-ui-react';

import { QUESTIONS, TRIAGE_QUESTION_ID } from '../fixtures';

import type { SharedProps } from './shared';

interface PastOrderOption {
  key: string;
  value: string;
  text: string;
}

interface PastOrderRaw {
  orderId: string;
  program?: string;
  memberName?: string;
  vendor?: string;
}

interface LinkOrderSectionProps extends SharedProps {
  pastOrders?: PastOrderOption[];
  pastOrdersRaw?: PastOrderRaw[];
  linkedOrderIds?: string[];
  onLinkedOrderIdsChange?: (ids: string[]) => void;
}

/**
 * Questions 62–64: Link another order, dropdown, feedback.
 * Shown when either path has reached 62 (received path after 61, or not-received path after 66b/56b).
 */
/** Formats a past order for display: Order ID → Program → Member Name → Vendor */
function formatOrderDisplay(o: PastOrderRaw): string {
  return `${o.orderId} → ${o.program || '—'} → ${o.memberName || '—'} → ${o.vendor || '—'}`;
}

export const LinkOrderSection: React.FC<LinkOrderSectionProps> = ({
  handleQuestion,
  handleTextInput,
  getAnswer,
  textInputs,
  pastOrders = [],
  pastOrdersRaw = [],
  linkedOrderIds = [],
  onLinkedOrderIdsChange,
}) => {
  const q62 = getAnswer(TRIAGE_QUESTION_ID.LINK_ANOTHER_ORDER);
  const q63 = getAnswer(TRIAGE_QUESTION_ID.PAST_ORDERS_IN_DROPDOWN);

  const orderLookup = React.useMemo(
    () => new Map(pastOrdersRaw.map((o) => [o.orderId, o])),
    [pastOrdersRaw]
  );

  return (
    <>
      <FormGroup grouped>
        <label>{QUESTIONS[TRIAGE_QUESTION_ID.LINK_ANOTHER_ORDER].questionText}</label>
        {QUESTIONS[TRIAGE_QUESTION_ID.LINK_ANOTHER_ORDER].options.map((option) => (
          <FormField
            key={option}
            label={option}
            control="input"
            type="radio"
            name="linkOrder"
            checked={q62 === option}
            onChange={() => handleQuestion(TRIAGE_QUESTION_ID.LINK_ANOTHER_ORDER, option)}
          />
        ))}
      </FormGroup>

      {q62 === 'Yes' && (
        <>
          <FormGroup grouped>
            <label>{QUESTIONS[TRIAGE_QUESTION_ID.PAST_ORDERS_IN_DROPDOWN].questionText}</label>
            {QUESTIONS[TRIAGE_QUESTION_ID.PAST_ORDERS_IN_DROPDOWN].options.map((option) => (
              <FormField
                key={option}
                label={option}
                control="input"
                type="radio"
                name="includeOrderIds"
                checked={q63 === option}
                onChange={() => handleQuestion(TRIAGE_QUESTION_ID.PAST_ORDERS_IN_DROPDOWN, option)}
              />
            ))}
          </FormGroup>

          {q63 === 'Yes' && onLinkedOrderIdsChange && (
            <FormGroup grouped>
              <label>{QUESTIONS[TRIAGE_QUESTION_ID.LINKED_ORDER_IDS].questionText}</label>
              <Dropdown
                placeholder="Search and select order IDs to link"
                fluid
                multiple
                search
                selection
                options={pastOrders}
                value={linkedOrderIds}
                noResultsMessage="No other orders available for this member."
                onChange={(_, { value }) => onLinkedOrderIdsChange((value as string[]) || [])}
              />
              {linkedOrderIds.length > 0 && (
                <List bulleted style={{ marginTop: 8 }}>
                  {linkedOrderIds.map((id) => {
                    const o = orderLookup.get(id);
                    return (
                      <List.Item key={id}>
                        {o ? formatOrderDisplay(o) : id}
                      </List.Item>
                    );
                  })}
                </List>
              )}
            </FormGroup>
          )}
        </>
      )}

      {(q62 === 'No' || (q62 === 'Yes' && q63)) && (
        <FormGroup grouped>
          <label>{QUESTIONS[TRIAGE_QUESTION_ID.ADDITIONAL_FEEDBACK].questionText}</label>
          <TextArea
            value={textInputs[TRIAGE_QUESTION_ID.ADDITIONAL_FEEDBACK] || ''}
            onChange={(e) => handleTextInput(TRIAGE_QUESTION_ID.ADDITIONAL_FEEDBACK, e.target.value)}
            placeholder="Is there anything else you think we should know about this triage?"
            style={{ minHeight: 100 }}
          />
        </FormGroup>
      )}
    </>
  );
};
